import type { TOptions } from 'i18next';
import type { AccountMismatch } from './sales-force-account-reconciliation';

export type AccountText = (key: string, options?: TOptions) => string;
export const accountFilters = ['linkage', 'fields', 'identity', 'contact', 'address', 'unresolved'] as const;
export type AccountFilter = typeof accountFilters[number];
const unrecognizedCountry = 'PostAddress.CountryId (unrecognized Salesforce code)';
const fieldNames: Record<string, string> = {
  CompanyName: 'companyName', OrgNr: 'organizationNumber', 'CompanyContact.Email': 'sellerEmail',
  CSN: 'csn', Place: 'visitingCity', 'PostAddress.Address1': 'postalAddress',
  'PostAddress.City': 'postalCity', 'PostAddress.ZipCode': 'postalCode',
  'PostAddress.CountryId': 'country', [unrecognizedCountry]: 'countryUnrecognized',
};

export function accountFieldName(field: string, text: AccountText): string {
  return fieldNames[field] ? text(`fields.${fieldNames[field]}`) : text('unknownField', { field });
}

export function accountValue(value: string | null, text: AccountText): string {
  return value == null ? text('notReported') : value.trim() === '' ? text('emptyValue') : value;
}

export function accountHasSource(row: AccountMismatch, system: 'SF' | 'WK'): boolean {
  switch (row.kind) {
    case 'FieldMismatch': return true;
    case 'UnmatchedSalesforce':
    case 'MissingInWebkua': return system === 'SF';
    case 'MissingInSalesforce': return system === 'WK';
    case 'DuplicateCompanyNumber': return system === 'SF'
      ? row.salesforceId != null || row.salesforceName != null
      : row.webkuaId != null || row.webkuaName != null;
  }
}

export function accountAbsentSource(row: AccountMismatch, text: AccountText): string {
  return text(row.kind === 'DuplicateCompanyNumber' || row.kind === 'UnmatchedSalesforce'
    || !row.companyNumber?.trim() ? 'notCompared' : 'notFound');
}

export function accountMissingValue(field: string, value: string | null): boolean {
  return !value?.trim() || (field === 'OrgNr'
    && ['no regist', 'no registered orgnr'].includes(value.trim().toLowerCase()));
}

export function accountCanSync(row: AccountMismatch): boolean {
  return row.kind === 'FieldMismatch' && !!row.salesforceId?.trim() && !!row.webkuaId?.trim()
    && !!row.companyNumber?.trim() && row.differences.length > 0
    && row.differences.every((difference) => !!fieldNames[difference.field] && difference.field !== unrecognizedCountry);
}

export function accountClearedFields(row: AccountMismatch): string[] {
  return row.differences.filter((difference) => accountMissingValue(difference.field, difference.salesforceValue)
    && !accountMissingValue(difference.field, difference.webkuaValue)).map((difference) => difference.field);
}

export function accountSyncBlockedTooltip(row: AccountMismatch, text: AccountText): string {
  if (!row.salesforceId?.trim() || !row.webkuaId?.trim() || !row.companyNumber?.trim())
    return text('sync.blockedLink');
  const reasons: string[] = [];
  if (row.differences.some((difference) => difference.field === unrecognizedCountry))
    reasons.push(text('unrecognizedCountry'));
  if (row.differences.some((difference) => !fieldNames[difference.field]))
    reasons.push(text('unknownComparisonField'));
  if (row.differences.length === 0) reasons.push(text('sync.errors.NoDifferences'));
  return reasons.length > 0 ? reasons.join(' ') : text('sync.blocked');
}

export function accountFindings(row: AccountMismatch, text: AccountText): string[] {
  switch (row.kind) {
    case 'UnmatchedSalesforce': return [text('missingSalesforceKey')];
    case 'MissingInWebkua': return [text('notFoundInWebkua')];
    case 'MissingInSalesforce': return [text(row.companyNumber?.trim() ? 'notFoundInSalesforce' : 'missingWebkuaKey')];
    case 'DuplicateCompanyNumber': return [text('ambiguousCustomer')];
    case 'FieldMismatch': {
      const findings = row.differences.map((difference) => {
        if (difference.field === unrecognizedCountry) {
          return text('unrecognizedCountry');
        }
        if (!fieldNames[difference.field]) return text('unknownComparisonField');
        const field = accountFieldName(difference.field, text);
        if (accountMissingValue(difference.field, difference.salesforceValue)) return text('missingField', { field, system: 'SF' });
        if (accountMissingValue(difference.field, difference.webkuaValue)) return text('missingField', { field, system: 'WK' });
        return text('fieldMismatch', { field });
      });
      if (row.billingCityMatchingPlace)
        findings.push(text('sync.billingCityWarning', { city: row.billingCityMatchingPlace }));
      return findings.length > 0 ? findings : [text('noFindingDetails')];
    }
  }
}

export function accountNextCheck(field: string, text: AccountText): string {
  const checks: Record<string, string> = {
    CompanyName: 'identity', OrgNr: 'identity', CSN: 'csn', 'CompanyContact.Email': 'contact',
    Place: 'visitingCity', 'PostAddress.Address1': 'address', 'PostAddress.City': 'address',
    'PostAddress.ZipCode': 'address', 'PostAddress.CountryId': 'country', [unrecognizedCountry]: 'country',
  };
  return text(`checks.${checks[field] ?? 'unknownField'}`);
}

export function matchesAccountFilter(row: AccountMismatch, filter: AccountFilter): boolean {
  const fields = row.differences.map((difference) => difference.field);
  switch (filter) {
    case 'linkage': return row.kind !== 'FieldMismatch';
    case 'fields': return fields.length > 0;
    case 'identity': return fields.some((field) => ['CompanyName', 'OrgNr', 'CSN'].includes(field));
    case 'contact': return fields.includes('CompanyContact.Email');
    case 'address': return fields.some((field) => field === 'Place' || field.startsWith('PostAddress.'));
    case 'unresolved': return row.kind === 'DuplicateCompanyNumber' || row.kind === 'UnmatchedSalesforce'
      || !row.companyNumber?.trim() || fields.includes(unrecognizedCountry)
      || fields.some((field) => !fieldNames[field]);
  }
}
